import { useEffect, useState } from "react";
import { toast } from "sonner";
import { admin } from "../../lib/api";
import { panel, btnPrimary, btnGhost, PageHead } from "../ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Download, Upload } from "lucide-react";

function StockTable() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dirty, setDirty] = useState({});

  const load = () => admin.get("/admin/inventory").then((r) => { setRows(r.data); setDirty({}); });
  useEffect(() => { load(); }, []);

  const key = (r) => `${r.product_id}|${r.size}`;
  const setStock = (r, v) => {
    const k = key(r);
    setRows(rows.map((x) => (key(x) === k ? { ...x, stock: v } : x)));
    setDirty({ ...dirty, [k]: { product_id: r.product_id, size: r.size, stock: Number(v) || 0 } });
  };
  const save = async () => {
    const payload = Object.values(dirty);
    if (!payload.length) { toast.info("No changes"); return; }
    await admin.put("/admin/inventory", payload);
    toast.success(`Updated ${payload.length} variants`);
    load();
  };
  const exportCsv = async () => {
    const r = await admin.get("/admin/inventory/export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "inventory.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const importCsv = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await admin.post("/admin/inventory/import", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${r.data.updated} variants`); load(); }
    catch { toast.error("Import failed"); }
  };

  const shown = rows.filter((r) => filter === "all" || (filter === "out" && r.stock === 0) || (filter === "low" && r.stock > 0 && r.stock <= r.low_threshold));

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["all", "low", "out"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-2 text-xs uppercase tracking-wider border ${filter === f ? "border-accent-ss text-accent" : "border-zinc-800 text-zinc-400"}`} style={filter === f ? { borderColor: "var(--ss-accent)" } : {}} data-testid={`inv-filter-${f}`}>{f === "all" ? "All" : f === "low" ? "Low Stock" : "Out of Stock"}</button>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={exportCsv} className={btnGhost + " flex items-center gap-1.5"} data-testid="inv-export-btn"><Download size={14} /> Export CSV</button>
          <label className={btnGhost + " flex items-center gap-1.5 cursor-pointer"} data-testid="inv-import-btn"><Upload size={14} /> Import<input type="file" accept=".csv" className="hidden" onChange={importCsv} /></label>
          <button onClick={save} className={btnPrimary} data-testid="inv-save-btn">Save Changes</button>
        </div>
      </div>
      <div className={`${panel} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
            <th className="p-3">Product</th><th className="p-3">SKU</th><th className="p-3">Size</th><th className="p-3">Stock</th>
          </tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={key(r)} className="border-b border-zinc-900">
                <td className="p-3 text-zinc-200">{r.product_name}</td>
                <td className="p-3 text-zinc-600 text-xs">{r.sku}</td>
                <td className="p-3 text-zinc-400">{r.size}</td>
                <td className="p-3">
                  <input type="number" value={r.stock} onChange={(e) => setStock(r, e.target.value)}
                    className={`w-20 bg-zinc-950 border px-2 py-1 text-sm ${r.stock === 0 ? "border-red-500/50 text-red-400" : r.stock <= r.low_threshold ? "border-amber-500/50 text-amber-400" : "border-zinc-800"}`}
                    data-testid={`inv-stock-${r.slug}-${r.size}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Waitlist() {
  const [list, setList] = useState([]);
  useEffect(() => { admin.get("/admin/notify-requests").then((r) => setList(r.data)); }, []);
  return (
    <div className={`${panel} overflow-x-auto`}>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
          <th className="p-3">Product</th><th className="p-3">Size</th><th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3">Requested</th>
        </tr></thead>
        <tbody>
          {list.map((w) => (
            <tr key={w.id} className="border-b border-zinc-900">
              <td className="p-3 text-zinc-200">{w.product_name}</td>
              <td className="p-3 text-zinc-400">{w.size || "—"}</td>
              <td className="p-3 text-zinc-400">{w.email}</td>
              <td className="p-3">{w.notified ? <span className="text-green-400 text-xs">Notified</span> : <span className="text-amber-400 text-xs">Waiting</span>}</td>
              <td className="p-3 text-zinc-600">{(w.created_at || "").slice(0, 10)}</td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-500">No back-in-stock requests yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Inventory() {
  return (
    <div>
      <PageHead title="Inventory" />
      <Tabs defaultValue="stock">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="stock" data-testid="tab-stock">Stock Table</TabsTrigger>
          <TabsTrigger value="waitlist" data-testid="tab-waitlist">Back-in-Stock Waitlist</TabsTrigger>
        </TabsList>
        <TabsContent value="stock" className="mt-5"><StockTable /></TabsContent>
        <TabsContent value="waitlist" className="mt-5"><Waitlist /></TabsContent>
      </Tabs>
    </div>
  );
}
